import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getBookInfo } from '../utils/bibleData';
import { createFamilyCompletionSubcollection, updateFamilyCompletion } from '../utils/familyCompletion';
import { CheckCircleIcon, ChartBarIcon, BookOpenIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

function CompletedChapters({ embedded = false, embeddedFamilyId = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const [selectedFamilyId, setSelectedFamilyId] = useState(embeddedFamilyId || null);
  const [familyData, setFamilyData] = useState(null);
  const [completionData, setCompletionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedChapters, setExpandedChapters] = useState(new Set());
  const [userFamilies, setUserFamilies] = useState([]);

  // Get family ID from URL params or use first family (skip when embedded)
  useEffect(() => {
    if (embedded) return;
    const familyId = searchParams.get('familyId');
    if (familyId) {
      setSelectedFamilyId(familyId);
    }
  }, [searchParams, embedded]);

  // Keep selectedFamilyId in sync when embeddedFamilyId changes
  useEffect(() => {
    if (embedded && embeddedFamilyId) {
      setSelectedFamilyId(embeddedFamilyId);
    }
  }, [embedded, embeddedFamilyId]);

  // Fetch user's families (skip when embedded — parent already manages family)
  useEffect(() => {
    if (embedded) return;
    async function fetchUserFamilies() {
      if (!currentUser) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const userData = userDoc.data();
        
        if (!userData || !userData.familyIds || userData.familyIds.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch all user's families
        const familiesData = [];
        for (const familyId of userData.familyIds) {
          try {
            const familyDoc = await getDoc(doc(db, 'families', familyId));
            if (familyDoc.exists()) {
              familiesData.push({ id: familyDoc.id, ...familyDoc.data() });
            }
          } catch (error) {
            console.error(`Error fetching family ${familyId}:`, error);
          }
        }
        
        setUserFamilies(familiesData);
        
        // Select family from URL or first family
        if (familiesData.length > 0) {
          const targetFamilyId = selectedFamilyId || familiesData[0].id;
          setSelectedFamilyId(targetFamilyId);
        }
        
      } catch (error) {
        console.error('Error fetching user families:', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchUserFamilies();
  }, [currentUser, selectedFamilyId]);

  // Fetch family completion data
  useEffect(() => {
    async function fetchCompletionData() {
      if (!selectedFamilyId) return;
      
      setLoading(true);
      try {
        console.log(`📊 COMPLETED CHAPTERS: Fetching data for family ${selectedFamilyId}`);
        
        // Get family data
        const familyDoc = await getDoc(doc(db, 'families', selectedFamilyId));
        if (familyDoc.exists()) {
          const familyData = { id: familyDoc.id, ...familyDoc.data() };
          setFamilyData(familyData);
          console.log('📊 COMPLETED CHAPTERS: Family data loaded:', familyData.name);
        } else {
          console.error('📊 COMPLETED CHAPTERS: Family document not found');
          setFamilyData(null);
        }

        // Get completion data from subcollection
        const completionRef = doc(db, `families/${selectedFamilyId}/completedChapters`, 'all-chapters');
        console.log(`📊 COMPLETED CHAPTERS: Reading from path: families/${selectedFamilyId}/completedChapters/all-chapters`);
        
        const completionDoc = await getDoc(completionRef);
        if (completionDoc.exists()) {
          const data = completionDoc.data();
          console.log('📊 COMPLETION DATA FOUND:', {
            hasChapters: !!data.chapters,
            chaptersCount: data.chapters?.length || 0,
            completedChapters: data.completedChapters || 0,
            totalChapters: data.totalChapters || 0,
            lastUpdated: data.lastUpdated
          });
          setCompletionData(data);
        } else {
          console.log('📊 COMPLETION DATA: No completion document found - attempting to create one...');
          try {
            // Try to create the completion subcollection
            await createFamilyCompletionSubcollection(selectedFamilyId, db);
            console.log('📊 COMPLETION DATA: Successfully created completion subcollection');
            
            // Try to fetch the data again
            const newCompletionDoc = await getDoc(completionRef);
            if (newCompletionDoc.exists()) {
              const data = newCompletionDoc.data();
              console.log('📊 COMPLETION DATA: Successfully loaded after creation:', {
                hasChapters: !!data.chapters,
                chaptersCount: data.chapters?.length || 0
              });
              setCompletionData(data);
            } else {
              console.error('📊 COMPLETION DATA: Still no data after creation attempt');
              setCompletionData(null);
            }
          } catch (createError) {
            console.error('📊 COMPLETION DATA: Failed to create completion subcollection:', createError);
            setCompletionData(null);
          }
        }
        
      } catch (error) {
        console.error('📊 COMPLETION DATA ERROR:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          familyId: selectedFamilyId
        });
        setCompletionData(null);
      } finally {
        setLoading(false);
      }
    }
    
    fetchCompletionData();
  }, [selectedFamilyId]);

  const handleFamilyChange = (familyId) => {
    setSelectedFamilyId(familyId);
    setExpandedChapters(new Set()); // Reset expanded chapters
  };

  const toggleChapter = (chapterKey) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(chapterKey)) {
      newExpanded.delete(chapterKey);
    } else {
      newExpanded.add(chapterKey);
    }
    setExpandedChapters(newExpanded);
  };

  const getCompletedChapters = () => {
    if (!completionData || !completionData.chapters) {
      console.log('📊 COMPLETED CHAPTERS: No completion data or chapters available');
      return [];
    }
    
    const completed = completionData.chapters.filter(chapter => chapter.completed);
    console.log(`📊 COMPLETED CHAPTERS: Found ${completed.length} completed chapters out of ${completionData.chapters.length} total`);
    return completed;
  };

  const getIncompleteChapters = () => {
    if (!completionData || !completionData.chapters) {
      console.log('📊 INCOMPLETE CHAPTERS: No completion data or chapters available');
      return [];
    }
    
    const incomplete = completionData.chapters.filter(chapter => !chapter.completed && chapter.completedVerses > 0);
    console.log(`📊 INCOMPLETE CHAPTERS: Found ${incomplete.length} in-progress chapters`);
    return incomplete;
  };

  const formatVerseNumbers = (verseNumbers) => {
    if (!verseNumbers || verseNumbers.length === 0) return 'No verses recorded';
    
    // Sort verse numbers and format as comma-separated list
    return verseNumbers.sort((a, b) => a - b).join(', ');
  };

  const getRecordedVerseNumbers = (chapter) => {
    // Handle both old and new data formats
    const recordedVerses = chapter.recordedVerseNumbers || chapter.recordedVerses || [];
    console.log(`📊 VERSE NUMBERS: Chapter ${chapter.book} ${chapter.chapter} has recorded verses:`, recordedVerses);
    return recordedVerses;
  };

  const getBookDisplayName = (bookId) => {
    const bookInfo = getBookInfo(bookId);
    return bookInfo ? bookInfo.name : bookId;
  };

  // Debug function to manually trigger completion update
  const handleDebugUpdate = async () => {
    if (!selectedFamilyId) return;
    
    try {
      console.log('🔧 DEBUG: Manually triggering completion update for Genesis 1...');
      await updateFamilyCompletion(selectedFamilyId, 'genesis', 1, [1, 2, 3], db);
      console.log('🔧 DEBUG: Completion update triggered, refreshing data...');
      
      // Refresh the data
      const completionRef = doc(db, `families/${selectedFamilyId}/completedChapters`, 'all-chapters');
      const completionDoc = await getDoc(completionRef);
      if (completionDoc.exists()) {
        const data = completionDoc.data();
        setCompletionData(data);
        console.log('🔧 DEBUG: Data refreshed after manual update');
      }
    } catch (error) {
      console.error('🔧 DEBUG: Error in manual update:', error);
    }
  };

  // Function to refresh completion data from recordings
  const handleRefreshFromRecordings = async () => {
    if (!selectedFamilyId) return;
    
    try {
      console.log('🔄 REFRESH: Refreshing completion data from recordings...');
      
      // Get all users in this family
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const usersSnapshot = await getDocs(
        query(
          collection(db, 'users'),
          where('familyIds', 'array-contains', selectedFamilyId)
        )
      );
      
      const familyUserIds = usersSnapshot.docs.map(doc => doc.id);
      console.log(`🔄 REFRESH: Found ${familyUserIds.length} family members`);
      
      if (familyUserIds.length === 0) {
        console.log('🔄 REFRESH: No family members found');
        return;
      }
      
      // Get ALL recordings from ALL family members
      const recordingsSnapshot = await getDocs(
        query(
          collection(db, 'recordings'),
          where('userId', 'in', familyUserIds)
        )
      );
      
      const allRecordings = recordingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log(`🔄 REFRESH: Found ${allRecordings.length} total recordings from family members`);
      
      // Group recordings by book and chapter
      const recordingsByChapter = {};
      for (const recording of allRecordings) {
        const key = `${recording.book}_${recording.chapter}`;
        if (!recordingsByChapter[key]) {
          recordingsByChapter[key] = [];
        }
        recordingsByChapter[key].push(recording);
      }
      
      console.log(`🔄 REFRESH: Recordings grouped into ${Object.keys(recordingsByChapter).length} unique chapters`);
      
      // Update completion data for each chapter that has recordings
      let updatedChapters = 0;
      for (const [chapterKey, chapterRecordings] of Object.entries(recordingsByChapter)) {
        const [book, chapter] = chapterKey.split('_');
        const chapterNum = parseInt(chapter);
        
        // Aggregate all recorded verses from all family members for this chapter
        const recordedVerses = new Set();
        for (const recording of chapterRecordings) {
          if (recording.verse) {
            recordedVerses.add(recording.verse);
          }
        }
        
        const recordedVerseNumbers = Array.from(recordedVerses).sort((a, b) => a - b);
        console.log(`🔄 REFRESH: Updating ${book} ${chapterNum} with verses: [${recordedVerseNumbers.join(', ')}]`);
        
        await updateFamilyCompletion(selectedFamilyId, book, chapterNum, recordedVerseNumbers, db);
        updatedChapters++;
      }
      
      console.log(`🔄 REFRESH: Updated ${updatedChapters} chapters, refreshing data...`);
      
      // Refresh the data
      const completionRef = doc(db, `families/${selectedFamilyId}/completedChapters`, 'all-chapters');
      const completionDoc = await getDoc(completionRef);
      if (completionDoc.exists()) {
        const data = completionDoc.data();
        setCompletionData(data);
        console.log('🔄 REFRESH: Data refreshed after update');
      }
      
    } catch (error) {
      console.error('🔄 REFRESH: Error refreshing from recordings:', error);
    }
  };

  if (!currentUser) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Please sign in to view completed chapters
        </h2>
        <p className="text-gray-600 dark:text-white/60">
          Create an account or sign in to see your family's progress.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Loading...</h2>
      </div>
    );
  }

  if (!embedded && userFamilies.length === 0) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Families Found</h2>
        <p className="text-gray-600 dark:text-white/60 mb-6">
          You haven't joined any families yet. Create your own family or join an existing one.
        </p>
        <div className="space-x-4">
          <button 
            className="btn-primary"
            onClick={() => navigate('/create-family')}
          >
            Create Family
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/join-family')}
          >
            Join Family
          </button>
        </div>
      </div>
    );
  }

  const completedChapters = getCompletedChapters();
  const incompleteChapters = getIncompleteChapters();

  return (
    <div className={embedded ? '' : 'min-h-screen bg-white dark:bg-[#0d1520]'}>
      {/* Standalone header — hidden when embedded in FamilyPage */}
      {!embedded && (
        <div className="bg-white dark:bg-[#0d1520] shadow-sm border-b border-gray-200 dark:border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/family')}
                  className="text-gray-600 dark:text-white/60 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Completed Chapters</h1>
                  {familyData && (
                    <p className="text-sm text-gray-600 dark:text-white/60">{familyData.name}</p>
                  )}
                </div>
              </div>

              {/* Family Selector */}
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700 dark:text-white/70">Family:</label>
                <select
                  value={selectedFamilyId || ''}
                  onChange={(e) => handleFamilyChange(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-white/5 border border-gray-300 dark:border-white/15 rounded-[3px] text-space-cadet dark:text-white text-sm focus:ring-2 focus:ring-cerulean focus:border-cerulean transition-all duration-200"
                >
                  {userFamilies.map(family => (
                    <option key={family.id} value={family.id}>
                      {family.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {!completionData ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <ChartBarIcon className="w-8 h-8 text-gray-400 dark:text-white/35" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Completion Data</h2>
            <p className="text-gray-600 dark:text-white/60">
              This family doesn't have completion tracking set up yet. Start recording to see progress here.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Summary Stats */}
            <div className="bg-white dark:bg-[#111827] rounded-[3px] p-6 shadow-sm border border-gray-100 dark:border-white/10">
              <h2 className="text-xl font-semibold text-space-cadet dark:text-white mb-4">Progress Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">{completedChapters.length}</div>
                  <div className="text-sm text-gray-600 dark:text-white/60">Chapters Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{incompleteChapters.length}</div>
                  <div className="text-sm text-gray-600 dark:text-white/60">Chapters in Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600">
                    {(() => {
                      if (!completionData?.chapters) return 0;
                      const bookMap = {};
                      completionData.chapters.forEach(ch => {
                        if (!bookMap[ch.book]) bookMap[ch.book] = { total: 0, completed: 0 };
                        bookMap[ch.book].total++;
                        if (ch.completed) bookMap[ch.book].completed++;
                      });
                      return Object.values(bookMap).filter(b => b.total > 0 && b.completed === b.total).length;
                    })()}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-white/60">Books Completed</div>
                </div>
              </div>
            </div>

            {/* Completed Chapters */}
            {completedChapters.length > 0 && (
              <div className="bg-white dark:bg-[#111827] rounded-[3px] p-6 shadow-sm border border-gray-100 dark:border-white/10">
                <h2 className="text-xl font-semibold text-space-cadet dark:text-white mb-6 flex items-center gap-2">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" /> Completed Chapters
                </h2>
                <div className="space-y-3">
                  {completedChapters.map((chapter) => {
                    const chapterKey = `${chapter.book}_${chapter.chapter}`;
                    const isExpanded = expandedChapters.has(chapterKey);
                    const bookName = getBookDisplayName(chapter.book);
                    
                    return (
                      <div key={chapterKey} className="border border-gray-200 dark:border-white/10 rounded-[3px]">
                        <button
                          onClick={() => toggleChapter(chapterKey)}
                          className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <CheckCircleSolid className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {bookName} Chapter {chapter.chapter}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-white/60">
                                {chapter.completedVerses}/{chapter.totalVerses} verses completed
                              </div>
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-400 dark:text-white/35 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-100 dark:border-white/10">
                            <div className="pt-3">
                              <div className="text-sm font-medium text-gray-700 dark:text-white/70 mb-2">Recorded Verses:</div>
                              <div className="text-sm text-gray-600 dark:text-white/60 bg-gray-50 dark:bg-white/5 p-3 rounded-[3px] font-mono">
                                {formatVerseNumbers(getRecordedVerseNumbers(chapter))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* In Progress Chapters */}
            {incompleteChapters.length > 0 && (
              <div className="bg-white dark:bg-[#111827] rounded-[3px] p-6 shadow-sm border border-gray-100 dark:border-white/10">
                <h2 className="text-xl font-semibold text-space-cadet dark:text-white mb-6 flex items-center gap-2">
                  <ArrowPathIcon className="w-6 h-6 text-cerulean" /> In Progress
                </h2>
                <div className="space-y-3">
                  {incompleteChapters.map((chapter) => {
                    const chapterKey = `${chapter.book}_${chapter.chapter}`;
                    const isExpanded = expandedChapters.has(chapterKey);
                    const bookName = getBookDisplayName(chapter.book);
                    
                    return (
                      <div key={chapterKey} className="border border-gray-200 dark:border-white/10 rounded-[3px]">
                        <button
                          onClick={() => toggleChapter(chapterKey)}
                          className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <ArrowPathIcon className="w-5 h-5 text-cerulean flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {bookName} Chapter {chapter.chapter}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-white/60">
                                {chapter.completedVerses}/{chapter.totalVerses} verses completed
                              </div>
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 text-gray-400 dark:text-white/35 transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-100 dark:border-white/10">
                            <div className="pt-3">
                              <div className="text-sm font-medium text-gray-700 dark:text-white/70 mb-2">Recorded Verses:</div>
                              <div className="text-sm text-gray-600 dark:text-white/60 bg-gray-50 dark:bg-white/5 p-3 rounded-[3px] font-mono">
                                {formatVerseNumbers(getRecordedVerseNumbers(chapter))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* No Progress */}
            {completedChapters.length === 0 && incompleteChapters.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpenIcon className="w-8 h-8 text-gray-400 dark:text-white/35" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Chapters Started</h2>
                <p className="text-gray-600 dark:text-white/60 mb-6">
                  This family hasn't started recording any chapters yet.
                </p>
                <div className="space-x-4">
                  <button
                    onClick={() => navigate('/record')}
                    className="bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white px-6 py-3 rounded-[3px] font-medium transition-colors"
                  >
                    Start Recording
                  </button>
                  <button
                    onClick={handleRefreshFromRecordings}
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-[3px] font-medium transition-colors text-sm"
                  >
                    Refresh from Recordings
                  </button>
                  {/* Debug button - only show in development */}
                  {process.env.NODE_ENV === 'development' && (
                    <button
                      onClick={handleDebugUpdate}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-[3px] font-medium transition-colors text-sm"
                    >
                      Debug: Add Test Data
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CompletedChapters;
